import React from 'react';
import { useSelector } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';

// Types
import { User } from 'Shared/constants/types';

// Selectors
import * as UsersSelectors from 'Shared/selectors/Users.selectors';

// Hooks
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useToggle } from 'Shared/utils/hooks';
import { useSmac } from 'Shared/utils/hooks/use-smac';

// Components
import { WpContainer } from 'Wpds/components/container';
import WpAvatarGroup from 'Wpds/components/avatar/WpAvatarGroup';
import WpIcon from 'Wpds/components/icon/WpIcon';
import WpButton from 'Wpds/components/button/WpButton';
import EditUserProfileModal from 'Dashboard/components/EditUserProfileModal/EditUserProfileModal';
import EditUserAvatarModal from 'Shared/components/EditUserAvatarModal/EditUserAvatarModal';

// Styles
import './UserDetails.scss';

type Props = {
  user: User;
};

const UserDetails = ({ user }: Props) => {
  const [isDetailsModalOpen, toggleIsDetailsModalOpen] = useToggle(false);
  const [isEditUserAvatarModalOpen, toggleIsEditUserAvatarModalOpen] = useToggle(false);
  const { id, avatar, managerId } = user;
  const userForAvatar = [
    { id, avatar: { color: avatar.color, initials: avatar.initials, profilePictureUrl: avatar.profilePictureUrl } },
  ];

  const { user: currentUser } = useCurrentUser();
  const manager = useSelector(state => UsersSelectors.getUserByUserId(state, { userId: managerId }))?.toJS();

  const isCurrentUser = id === currentUser.id;

  const hasTitleListed = !!user.title;
  const hasDeptListed = !!user.department;
  const hasManagerListed = !!manager;

  const baseClassName = 'UserDetails';

  const { isStandAlone, hasPi2Access } = useSmac();
  const { enablePerformOrgManagement } = useFlags();

  const disableEdit = enablePerformOrgManagement && (isStandAlone || hasPi2Access);

  return (
    <>
      <div className={baseClassName}>
        <div className={`${baseClassName}__header ${baseClassName}__profile-container`}>
          <div className={`${baseClassName}__header__user`}>
            {/* using WpAvatarGroup to get the white border */}
            <div
              className={`${baseClassName}__header__user__avatar-wrapper`}
              onClick={isCurrentUser ? toggleIsEditUserAvatarModalOpen : undefined}
            >
              <WpAvatarGroup users={userForAvatar} size="xxxl" />
              {isCurrentUser && (
                <div className={`${baseClassName}__header__user__avatar-wrapper__edit-wrapper`}>
                  <WpIcon name="upload" size="lg" />
                </div>
              )}
            </div>
            <h4 className={`${baseClassName}__header__user__name`}>{user.fullName}</h4>
            <span className={`${baseClassName}__header__user__title`}>{user.email}</span>
          </div>
        </div>

        <WpContainer className={`${baseClassName}__profile-container`}>
          <div className={`${baseClassName}__detail-list`}>
            {isCurrentUser && !disableEdit && (
              <div className={`${baseClassName}__detail-list__edit-btn`}>
                <WpButton size="sm" variant="tertiary" onClick={toggleIsDetailsModalOpen}>
                  <WpIcon name="pencil" />
                  Edit
                </WpButton>
              </div>
            )}
            <div className={`${baseClassName}__detail-list__item`}>
              <div className={`${baseClassName}__detail-list__item__icon`}>
                <WpIcon name="briefcase" size="lg" />
              </div>

              <div className={`${baseClassName}__detail-list__item__name`}>
                <span className={`${baseClassName}__detail-list__item__name__label`}>Title</span>

                <p className={`${baseClassName}__detail-list__item__name__value`}>
                  {hasTitleListed ? user.title : '--'}
                </p>
              </div>
            </div>
            <div className={`${baseClassName}__detail-list__item`}>
              <div className={`${baseClassName}__detail-list__item__icon`}>
                <WpIcon name="building" size="lg" />
              </div>

              <div className={`${baseClassName}__detail-list__item__name`}>
                <span className={`${baseClassName}__detail-list__item__name__label`}>Department</span>

                <p className={`${baseClassName}__detail-list__item__name__value`}>
                  {hasDeptListed ? user.department : '--'}
                </p>
              </div>
            </div>
            <div className={`${baseClassName}__detail-list__item`}>
              <div className={`${baseClassName}__detail-list__item__icon`}>
                <WpIcon name="user" size="lg" />
              </div>
              <div className={`${baseClassName}__detail-list__item__name`}>
                <span className={`${baseClassName}__detail-list__item__name__label`}>Manager</span>
                <p className={`${baseClassName}__detail-list__item__name__value`}>
                  {hasManagerListed ? manager.fullName : '--'}
                </p>
              </div>
            </div>
          </div>
        </WpContainer>
      </div>
      {isDetailsModalOpen && (
        <EditUserProfileModal user={user} isOpen={isDetailsModalOpen} toggleIsOpen={toggleIsDetailsModalOpen} />
      )}
      {isEditUserAvatarModalOpen && (
        <EditUserAvatarModal
          user={user}
          isOpen={isEditUserAvatarModalOpen}
          toggleIsOpen={toggleIsEditUserAvatarModalOpen}
        />
      )}
    </>
  );
};

export default UserDetails;
